import React, { useState, useEffect } from "react"
import { navigate } from "vike/client/router"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import Value from "aldoo-ra/Value"
import {
  ChartBarIcon,
  TicketIcon,
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PauseIcon,
  ExclamationTriangleIcon,
  PlusCircleIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline"
import { GetStatistics } from "./api"
import { showNotification } from "aldoo-ra/Notification"

export default function LicenseOverview() {
  const [statistics, setStatistics] = Value("license-statistics", null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Base URL for navigation
  const baseUrl = "/admin/product-licensing"

  // Navigate to filtered views
  const navigateToLicenses = (filterParams) => {
    if (typeof window !== "undefined") {
      const queryString = new URLSearchParams(filterParams).toString()
      const url = queryString
        ? `${baseUrl}/licenses?${queryString}`
        : `${baseUrl}/licenses`
      navigate(url)
    }
  }

  // Load statistics
  const loadStatistics = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await GetStatistics()

      if (response.result) {
        setStatistics(response.data)
      } else {
        setError(response.error || "Failed to load statistics")
        showNotification({
          message:
            "Failed to load licensing statistics: " +
            (response.error || "Unknown error"),
          className: "bg-admin_error text-white",
        })
      }
    } catch (err) {
      setError("Network error: " + err.message)
      showNotification({
        message: "Failed to load licensing statistics: " + err.message,
        className: "bg-admin_error text-white",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadStatistics()
  }, [])

  // Helper function to format numbers
  const formatNumber = (num) => {
    if (num === null || num === undefined) return "0"
    if (typeof num === "number") {
      return num.toLocaleString()
    }
    return String(num)
  }

  // Reusable StatCard component template for Additional Stats Row
  const StatCard = ({ 
    title, 
    value, 
    subtitle, 
    valueColor = "text-admin_primary",
    titleVariant = "admin_body_accent",
    valueVariant = "admin_h3",
    subtitleVariant = "admin_txt1_dim",
    subtitleClassName = "",
    layout = "flex" // "standard" or "flex"
  }) => {
    const cardContent = (
      <>
        <Typography
          variant={titleVariant}
          className="mt-0 mb-2"
        >
          {title}
        </Typography>

        <Typography 
          component="div"
          variant={valueVariant} 
          className={valueColor}
        >
          {value}
        </Typography>

        <Typography
          variant={subtitleVariant}
          className={`"text-admin_text_secondary mt-1" ${subtitleClassName}`}
        >
          {subtitle}
        </Typography>
      </>
    )

    return (
      <div className={`bg-admin_paper rounded-xl shadow-sm p-6 ${layout === "flex" ? "flex flex-col justify-between" : ""}`}>
        {cardContent}
      </div>
    )
  }

  // Define KPI cards with real data access
  const licenseCards = [
    {
      id: "totalLicenses",
      title: "Total Licenses",
      getValue: (stats) => {
        const total = stats?.overview?.licenses?.total
        return formatNumber(total || 0)
      },
      subValue: (stats) => {
        const activation = stats?.overview?.activationRate
        return `Activation Rate: ${activation || 0}%`
      },
      bgColor: "bg-admin_paper",
      textColor: "text-admin_success",
      icon: <TicketIcon className="w-8 h-8 text-admin_success" />,
      onClick: () => navigateToLicenses({}),
      isClickable: true,
    },
    {
      id: "totalSeats",
      title: "Total Seats",
      getValue: (stats) => {
        const total = stats?.overview?.seats?.total
        return formatNumber(total || 0)
      },
      subValue: (stats) => {
        const utilization = stats?.overview?.seats?.utilization
        return `Utilization: ${utilization || 0}%`
      },
      bgColor: "bg-admin_paper",
      textColor: "text-admin_primary",
      icon: <UserGroupIcon className="w-8 h-8 text-admin_primary" />,
      isClickable: false,
    },
  ]

  const statusCards = [
    {
      id: "active",
      title: "Active",
      getValue: (stats) => {
        const active = stats?.overview?.licenses?.active
        return formatNumber(active || 0)
      },
      bgColor: "bg-admin_paper",
      textColor: "text-admin_success",
      icon: <CheckCircleIcon className="w-6 h-6" />,
      onClick: () => navigateToLicenses({ status: "active" }),
      isClickable: true,
    },
    {
      id: "paused",
      title: "Paused",
      getValue: (stats) => {
        const paused = stats?.overview?.licenses?.paused
        return formatNumber(paused || 0)
      },
      bgColor: "bg-admin_paper",
      textColor: "text-admin_warning",
      icon: <PauseIcon className="w-6 h-6" />,
      onClick: () => navigateToLicenses({ status: "paused" }),
      isClickable: true,
    },
    {
      id: "expired",
      title: "Expired",
      getValue: (stats) => {
        const expired = stats?.overview?.licenses?.expired
        return formatNumber(expired || 0)
      },
      bgColor: "bg-admin_paper",
      textColor: "text-admin_error",
      icon: <XCircleIcon className="w-6 h-6" />,
      onClick: () => navigateToLicenses({ status: "expired" }),
      isClickable: true,
    },
    {
      id: "revoked",
      title: "Revoked",
      getValue: (stats) => {
        const revoked = stats?.overview?.licenses?.revoked
        return formatNumber(revoked || 0)
      },
      bgColor: "bg-admin_paper",
      textColor: "text-admin_text_secondary",
      icon: <XCircleIcon className="w-6 h-6" />,
      onClick: () => navigateToLicenses({ status: "revoked" }),
      isClickable: true,
    },
    {
      id: "free",
      title: "Available",
      getValue: (stats) => {
        const free = stats?.overview?.licenses?.free
        return formatNumber(free || 0)
      },
      bgColor: "bg-admin_paper",
      textColor: "text-admin_progress",
      icon: <TicketIcon className="w-6 h-6" />,
      onClick: () => navigateToLicenses({ status: "free" }),
      isClickable: true,
    },
  ]

  // Loading state
  if (loading) {
    return (
      <div className="p-8 bg-admin_canvas h-full">
        <div className="flex justify-between items-center mb-8">
          <div>
            <div className="h-8 bg-admin_neutral_1 rounded animate-pulse w-48 mb-2"></div>
            <div className="h-4 bg-admin_neutral_1 rounded animate-pulse w-32"></div>
          </div>
          <div className="h-10 bg-admin_neutral_1 rounded animate-pulse w-40"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {[...Array(2)].map((_, i) => (
            <div
              key={i}
              className="bg-admin_paper rounded-xl h-36 shadow-sm animate-pulse"
            >
              <div className="p-6">
                <div className="h-6 bg-admin_neutral_1 rounded w-32 mb-4"></div>
                <div className="h-10 bg-admin_neutral_1 rounded w-24 mb-2"></div>
                <div className="h-4 bg-admin_neutral_1 rounded w-40"></div>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-admin_paper rounded-xl p-6 shadow-sm">
          <div className="h-6 bg-admin_neutral_1 rounded w-48 mb-6"></div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="p-4 rounded-lg border border-admin_bordered"
              >
                <div className="h-4 bg-admin_neutral_1 rounded w-16 mb-2"></div>
                <div className="h-8 bg-admin_neutral_1 rounded w-12"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  // Error state (no cached data)
  if (error && !statistics) {
    return (
      <div className="p-8 bg-admin_canvas h-full">
        <div className="flex justify-between items-center mb-8">
          <Typography component="h1" variant="admin_h2">
            License Overview
          </Typography>

          <Button
            variant="admin_primary"
            onClick={() => navigate(`${baseUrl}/licenses/generate`)}
          >
            Add License
            <PlusCircleIcon className="w-5 h-5 mr-2" />
          </Button>
        </div>

        <div className="bg-admin_error/10 border border-admin_error/20 rounded-xl p-8 text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-admin_error mx-auto mb-4" />

          <Typography 
            variant="admin_h4" 
            className="text-admin_error mb-2"
          >
            Failed to Load Statistics
          </Typography>

          <Typography
            variant="admin_body_dim"
            className="max-w-md mb-6 mx-auto"
          >
            {error}
          </Typography>

          <div className="flex justify-center space-x-3">
            <Button variant="admin_primary" onClick={loadStatistics}>
              <ArrowPathIcon className="w-5 h-5 mr-2" />

              Retry
            </Button>
            
            <Button
              variant="admin_secondary"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const quickActionButtons = [
    {
      id: "manage-packs",
      icon: ChartBarIcon,
      title: "Manage License Packs",
      subtitle: "Organize and distribute licenses",
      onClick: () => navigate(`${baseUrl}/packs`),
    },
    {
      id: "configure-models",
      icon: ClockIcon,
      title: "Configure Models",
      subtitle: "Set up license templates",
      onClick: () => navigate(`${baseUrl}/models`),
    },
    {
      id: "browse-licenses",
      icon: TicketIcon,
      title: "Browse All Licenses",
      subtitle: "View and manage individual licenses",
      onClick: () => navigate(`${baseUrl}/licenses`),
    },
  ];

  // QuickActionButton component
  const QuickActionButton = ({ icon: Icon, title, subtitle, onClick }) => (
    <Button
      variant="admin_secondary"
      className="h-auto justify-start group p-4"
      onClick={onClick}
    >
      <Icon className="w-7 h-7 xl:w-10 xl:h-10 mr-3" />
      
      <div className="text-left">
        <Typography 
          className="text-base xl:text-lg text-admin_primary font-medium group-hover:text-white"
        >
          {title}
        </Typography>
        
        <Typography 
          className="block text-sm text-admin_text_secondary group-hover:text-white/80"
        >
          {subtitle}
        </Typography>
      </div>
    </Button>
  );

  // Main render with data
  return (
    <div className="h-full bg-admin_canvas p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <Typography component="h1" variant="admin_h2">
            License Overview
          </Typography>

          <Typography
            variant="admin_body_dim"
            className="mt-1 mb-0"
          >
            Monitor and manage your licensing system
          </Typography>
        </div>

        <div className="flex gap-3">
          <Button
            variant="admin_secondary"
            onClick={loadStatistics}
            disabled={loading}
          >
            <ArrowPathIcon className="w-5 h-5 mr-2" />

            Refresh
          </Button>

          <Button
            variant="admin_primary"
            onClick={() => navigate(`${baseUrl}/licenses/generate`)}
          >
            <PlusCircleIcon className="w-5 h-5 mr-2" />

            Add License
          </Button>
        </div>
      </div>

      {/* Big Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {licenseCards.map((card) => (
          <div
            key={card.id}
            className="bg-admin_paper rounded-xl shadow-sm hover:shadow-md transition-shadow p-6"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="flex-1">
                <Typography 
                  variant="admin_h4" 
                  className="mb-2"
                >
                  {card.title}
                </Typography>

                <Typography 
                  component="div"
                  variant="admin_h1" 
                  className={card.textColor}
                >
                  {card.getValue(statistics)}
                </Typography>

                <Typography
                  variant="admin_body"
                  className="text-admin_text_secondary mt-2"
                >
                  {card.subValue(statistics)}
                </Typography>
              </div>

              {card.icon && (
                <div className="bg-admin_canvas rounded-lg p-3 ml-4">
                  {card.icon}
                </div>
              )}
            </div>
            
            {card.isClickable && (
              <Button
                variant="text"
                className="text-admin_primary hover:underline text-sm font-medium"
                onClick={card.onClick}
              >
                View Details →
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Status Breakdown */}
      <div className="bg-admin_paper rounded-xl shadow-sm p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <Typography variant="admin_h4">
            License Status Breakdown
          </Typography>

          <Typography variant="admin_txt1_dim">
            Click any status to view detailed licenses
          </Typography>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {statusCards.map((card) => (
            <div
              key={card.id}
              className={`p-4 rounded-lg border border-admin_bordered transition-all ${
                card.isClickable
                  ? "cursor-pointer hover:shadow-md hover:border-admin_primary/50 hover:bg-admin_canvas/50"
                  : ""
              }`}
              onClick={card.onClick}
            >
              <div className="flex items-center justify-between mb-4">
                <Typography
                  variant="admin_body_accent"
                  className="my-0"
                >
                  {card.title}
                </Typography>

                {card.icon && <div className={card.textColor}>{card.icon}</div>}
              </div>

              <Typography 
                component="div"
                variant="admin_h3" 
                className={card.textColor}
              >
                {card.getValue(statistics)}
              </Typography>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <StatCard
          title="License Packs"
          value={formatNumber(statistics?.overview?.packs?.total || 0)}
          subtitle={`${formatNumber(statistics?.overview?.packs?.active || 0)} active`}
          valueColor="text-admin_primary"
          titleVariant="admin_body_accent"
          layout="flex"
        />

        <StatCard
          title="License Models"
          value={formatNumber(statistics?.overview?.models || 0)}
          subtitle="Available templates"
          valueColor="text-admin_success"
        />

        <StatCard
          title="Expiring Soon"
          value={formatNumber(statistics?.overview?.expiringInNext30Days || 0)}
          subtitle="Next 30 days"
          valueColor="text-admin_warning"
        />

        <StatCard
          title="System Health"
          value={`${statistics?.summary?.health?.score || 0}%`}
          subtitle={statistics?.summary?.health?.status || "Unknown"}
          valueColor={
            statistics?.summary?.health?.status === "excellent"
              ? "text-admin_success"
              : statistics?.summary?.health?.status === "good"
              ? "text-admin_primary"
              : statistics?.summary?.health?.status === "fair"
              ? "text-admin_warning"
              : "text-admin_error"
          }
          subtitleClassName="capitalize"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {quickActionButtons.map((button) => (
          <QuickActionButton
            key={button.id}
            icon={button.icon}
            title={button.title}
            subtitle={button.subtitle}
            onClick={button.onClick}
          />
        ))}
      </div>

      {/* Error banner if refresh fails but we have cached data */}
      {error && statistics && (
        <div className="mt-8 bg-admin_warning/10 border border-admin_warning/20 p-4 rounded-lg flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-admin_warning" />
            <div>
              <Typography
                variant="admin_body"
                className="text-admin_warning font-medium"
              >
                Unable to refresh statistics
              </Typography>

              <Typography
                variant="admin_txt1"
                className="text-admin_text_secondary"
              >
                Showing cached data. {error}
              </Typography>
            </div>
          </div>

          <Button 
            variant="admin_secondary" 
            onClick={loadStatistics}
          >
            <ArrowPathIcon className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
      )}
    </div>
  )
}
